import React, { useCallback, useEffect, useState } from "react";
import { fetchFeedRejections, fetchFeedReports, resolveFeedReport } from "../../api";
import Surface from "../../theme/Surface";

/**
 * FeedAdminReview — the moderation surface (staff only). Two tabs:
 *   Reports    — the open-report queue; resolve with hide/unhide/remove/
 *                dismiss/suspend. Spans BOTH genders (the one surface that
 *                does), with the author's gender shown per row.
 *   Rejections — recent fuṣḥā rejections with their marker_hits: the evidence
 *                trail for tuning the Tier-A dialect list.
 */
export default function FeedAdminReview({ onClose }) {
  const [tab, setTab] = useState("reports"); // reports | rejections
  const [reports, setReports] = useState([]);
  const [rejections, setRejections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const fetcher =
      tab === "reports"
        ? fetchFeedReports("open").then((d) => setReports(d.reports || []))
        : fetchFeedRejections().then((d) => setRejections(d.rejections || []));
    fetcher.catch((e) => setError(e.message)).finally(() => setLoading(false));
  }, [tab]);

  useEffect(load, [load]);

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const act = async (report, action) => {
    let suspendDays = null;
    let note = "";
    if (action === "suspend") {
      const raw = window.prompt("Suspend for how many days? (blank = indefinite)", "7");
      if (raw === null) return;
      suspendDays = raw.trim() ? parseInt(raw, 10) : null;
      note = window.prompt("Reason (shown to the user):", "Repeated non-fuṣḥā posting") || "";
    }
    setBusyId(report.id);
    try {
      await resolveFeedReport(report.id, { action, note, suspendDays });
      setReports((prev) => prev.filter((r) => r.id !== report.id));
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-[3px] p-4"
      onClick={onClose}
    >
      <div
        className="surface-glass dark-scope rounded-2xl shadow-2xl w-full max-w-3xl h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="flex items-center justify-between px-6 py-4 shrink-0"
          style={{ borderBottom: "1px solid var(--panel-line)" }}
        >
          <div>
            <h2 className="font-cormorant font-semibold text-lg" style={{ color: "var(--gold-bright)" }}>
              Feed Moderation
            </h2>
            <p className="text-xs" style={{ color: "var(--parchment-dim)" }}>
              Reports queue + fuṣḥā-rejection tuning · spans both genders
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg transition-[filter] hover:brightness-125"
            style={{ color: "var(--parchment-dim)" }}
            aria-label="Close"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-6 pt-3 shrink-0">
          <div className="surface-glass flex rounded-xl overflow-hidden text-sm max-w-xs">
            {[["reports", "Reports"], ["rejections", "Rejections"]].map(([key, label]) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                className="flex-1 px-3 py-2 transition-colors"
                style={
                  tab === key
                    ? { color: "var(--gold-bright)", background: "var(--gold-bg)" }
                    : { color: "var(--parchment-dim)" }
                }
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto scroll-elegant px-6 py-4 space-y-3">
          {loading && (
            <p className="text-sm text-center py-8" style={{ color: "var(--parchment-dim)" }}>Loading…</p>
          )}
          {error && (
            <p className="text-sm text-center" style={{ color: "var(--tag-rose)" }}>{error}</p>
          )}

          {!loading && tab === "reports" && reports.length === 0 && (
            <p className="text-sm text-center py-8" style={{ color: "var(--parchment-dim)" }}>
              No open reports — الحمد لله.
            </p>
          )}
          {!loading && tab === "reports" &&
            reports.map((report) => (
              <Surface key={report.id} className="rounded-2xl px-4 py-3">
                <div className="flex items-center gap-2 text-xs mb-1.5 flex-wrap"
                     style={{ color: "var(--parchment-dim)" }}>
                  <span className="px-2 py-0.5 rounded-full"
                        style={{ background: "color-mix(in srgb, var(--tag-rose) 18%, transparent)",
                                 color: "var(--tag-rose)" }}>
                    {report.reason}
                  </span>
                  <span>{report.target.type}</span>
                  <span className="px-2 py-0.5 rounded-full"
                        style={{ background: "color-mix(in srgb, var(--tag-teal) 18%, transparent)",
                                 color: "var(--tag-teal)" }}>
                    {report.target.author_gender}
                  </span>
                  <span>by @{report.target.author?.handle || "؟"}</span>
                  <span>· {report.target.post_report_count} report(s) on post</span>
                  <span>· status: {report.target.status}</span>
                </div>
                <p dir="rtl" lang="ar" className="font-arabic text-base leading-relaxed"
                   style={{ color: "var(--parchment)" }}>
                  {report.target.body}
                </p>
                {report.detail && (
                  <p className="text-xs mt-1" style={{ color: "var(--parchment-dim)" }}>
                    Reporter: “{report.detail}”
                  </p>
                )}
                <div className="flex gap-2 mt-2 flex-wrap">
                  {(report.target.status === "hidden"
                    ? ["unhide", "remove", "dismiss", "suspend"]
                    : ["hide", "remove", "dismiss", "suspend"]
                  ).map((action) => (
                    <button
                      key={action}
                      onClick={() => act(report, action)}
                      disabled={busyId === report.id}
                      className="dt-ghost-btn text-xs px-2.5 py-1.5 rounded-lg disabled:opacity-50 capitalize"
                    >
                      {action}
                    </button>
                  ))}
                </div>
              </Surface>
            ))}

          {!loading && tab === "rejections" && rejections.length === 0 && (
            <p className="text-sm text-center py-8" style={{ color: "var(--parchment-dim)" }}>
              No rejections recorded yet.
            </p>
          )}
          {!loading && tab === "rejections" &&
            rejections.map((r) => (
              <Surface key={r.id} className="rounded-2xl px-4 py-3">
                <div className="flex items-center gap-2 text-xs mb-1.5"
                     style={{ color: "var(--parchment-dim)" }}>
                  <span>{r.target}</span>
                  <span>· stage: {r.stage}</span>
                  {r.score != null && <span>· score: {r.score}</span>}
                  {r.marker_hits?.length > 0 && (
                    <span className="font-arabic" style={{ color: "var(--tag-rose)" }}>
                      · {r.marker_hits.join("، ")}
                    </span>
                  )}
                </div>
                <p dir="rtl" lang="ar" className="font-arabic text-base leading-relaxed"
                   style={{ color: "var(--parchment)" }}>
                  {r.body}
                </p>
              </Surface>
            ))}
        </div>
      </div>
    </div>
  );
}
